import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useRigDetail, useRigs } from '../lib/api';
import RigMiniMap from '../RigMiniMap';
import type {
  ContractDto,
  NameHistoryItemDto,
  RigCardDto,
  RigDetailDto,
  RigStatus,
  RigType,
  SpecFieldKey,
} from '../types';
import { ConfidenceValue, SourceLink } from './_shared';

// Tab 5 — Rig Fleet. Left filter panel (type/status/company/region + equipment facets
// populated from values found across the fleet + numeric water-depth / hook-load filters)
// over a card grid; /wwd/rigs/:slug opens the full spec-sheet modal (34 catalog fields +
// contracts, every value through ConfidenceValue). Cards show the vision-verified rig
// photo when the photo hunt has found one.

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

const labelClass = 'text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)';

const RIG_TYPE_LABELS: Record<RigType, string> = {
  drillship: 'Drillship',
  semisub: 'Semisub',
  jackup: 'Jack-up',
  platform: 'Platform',
  tender_assist: 'Tender-assist',
  spar: 'Spar',
};

const RIG_STATUS_LABELS: Record<RigStatus, string> = {
  active: 'Active',
  stacked: 'Stacked',
  available: 'Available',
  under_construction: 'Under construction',
  retired: 'Retired',
};

const RIG_STATUS_STYLES: Record<RigStatus, string> = {
  active: 'border-score-high text-score-high',
  stacked: 'border-border-strong text-muted',
  available: 'border-accent-info text-accent-info',
  under_construction: 'border-accent-warn text-accent-warn',
  retired: 'border-border text-muted opacity-70',
};

type Category = '' | 'floaters' | 'jackups' | 'platforms';

const CATEGORY_PILLS: { key: Exclude<Category, ''>; label: string }[] = [
  { key: 'floaters', label: 'Floaters' },
  { key: 'jackups', label: 'Jack-ups' },
  { key: 'platforms', label: 'Platforms' },
];

function matchesCategory(r: RigCardDto, c: Category): boolean {
  if (c === '') return true;
  if (c === 'floaters') return r.rigType === 'drillship' || r.rigType === 'semisub';
  if (c === 'jackups') return r.rigType === 'jackup';
  return r.rigType === 'platform';
}

type SortKey = 'name' | 'drillco' | 'client' | 'rigType' | 'region' | 'designClass' | 'yearBuilt' | 'waterDepth';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'drillco', label: 'Drill Co.' },
  { key: 'client', label: 'Oil Co.' },
  { key: 'rigType', label: 'Rig Type' },
  { key: 'region', label: 'Region' },
  { key: 'designClass', label: 'Design Class' },
  { key: 'yearBuilt', label: 'Year Built' },
  { key: 'waterDepth', label: 'Water Depth' },
];

function sortValue(r: RigCardDto, k: SortKey): string | number | null {
  switch (k) {
    case 'name':
      return r.name;
    case 'drillco':
      return r.drillco.name;
    case 'client':
      return r.currentClient?.name ?? null;
    case 'rigType':
      return RIG_TYPE_LABELS[r.rigType];
    case 'region':
      return r.region?.name ?? null;
    case 'designClass':
      return r.designClass;
    case 'yearBuilt':
      return r.yearBuilt;
    case 'waterDepth':
      return r.waterDepthM;
  }
}

/** Nulls always sort last, regardless of direction. */
function compareNullsLast(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

const SPEC_LABEL_OVERRIDES: Partial<Record<SpecFieldKey, string>> = {
  bop: 'BOP',
  dp_system: 'DP System',
  imo_number: 'IMO Number',
  mmsi: 'MMSI',
};

/* ---------- equipment facets (left panel dropdowns, populated from found values) ---------- */

type EquipKey =
  | 'drawworks'
  | 'topDrive'
  | 'mudPumpModel'
  | 'heaveCompensator'
  | 'bopModel'
  | 'drawworksBrand'
  | 'topDriveBrand'
  | 'mudPumpBrand'
  | 'heaveCompBrand'
  | 'bopBrand';

// Brand dropdowns sit above their model dropdown — brands are acquisition rollups
// (HMH covers MH/Wirth/Aker MH/MHWirth heritage; NOV covers National/Varco/Hydralift),
// so one brand entry spans many models and shrinks the option lists.
const EQUIP_FILTERS: { key: EquipKey; label: string }[] = [
  { key: 'drawworksBrand', label: 'Drawworks brand' },
  { key: 'drawworks', label: 'Drawworks' },
  { key: 'topDriveBrand', label: 'Top drive brand' },
  { key: 'topDrive', label: 'Top drive' },
  { key: 'mudPumpBrand', label: 'Mud pump brand' },
  { key: 'mudPumpModel', label: 'Mud pump model' },
  { key: 'heaveCompBrand', label: 'Heave comp. brand' },
  { key: 'heaveCompensator', label: 'Heave compensator' },
  { key: 'bopBrand', label: 'BOP brand' },
  { key: 'bopModel', label: 'BOP model' },
];

/** Distinct canonical products for one equipment field, most common first. */
function equipOptions(rigs: RigCardDto[], key: EquipKey): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const r of rigs) {
    const v = r[key];
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** Distinct mud-pump counts found in the fleet, ascending. */
function pumpCountOptions(rigs: RigCardDto[]): number[] {
  const set = new Set<number>();
  for (const r of rigs) if (r.mudPumpCount !== null) set.add(r.mudPumpCount);
  return [...set].sort((a, b) => a - b);
}

function truncate(v: string, max = 58): string {
  return v.length > max ? `${v.slice(0, max - 3)}…` : v;
}

function specLabel(key: SpecFieldKey): string {
  const override = SPEC_LABEL_OVERRIDES[key];
  if (override) return override;
  return key
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export default function RigFleet() {
  const rigsQuery = useRigs();
  const rigs = useMemo(() => rigsQuery.data?.rigs ?? [], [rigsQuery.data]);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const detailQuery = useRigDetail(slug);

  // Deep-linkable drillco filter — Companies cards link here as /wwd/rigs?drillco=<slug>.
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState<Category>('');
  const [rigType, setRigType] = useState('');
  const [status, setStatus] = useState('');
  const [drillco, setDrillco] = useState(searchParams.get('drillco') ?? '');
  const [client, setClient] = useState('');
  const [region, setRegion] = useState('');
  const [minWaterDepth, setMinWaterDepth] = useState('');
  const [minHookLoad, setMinHookLoad] = useState('');
  const [activeHeave, setActiveHeave] = useState(false);
  const [mudPumpCount, setMudPumpCount] = useState('');
  const [equip, setEquip] = useState<Record<EquipKey, string>>({
    drawworks: '',
    topDrive: '',
    mudPumpModel: '',
    heaveCompensator: '',
    bopModel: '',
    drawworksBrand: '',
    topDriveBrand: '',
    mudPumpBrand: '',
    heaveCompBrand: '',
    bopBrand: '',
  });
  const [sort, setSort] = useState<SortKey>('name');

  const drillcoOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rigs) m.set(r.drillco.slug, r.drillco.name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rigs]);

  const clientOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rigs) if (r.currentClient) m.set(r.currentClient.slug, r.currentClient.name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rigs]);

  const regionOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rigs) if (r.region) m.set(r.region.slug, r.region.name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rigs]);

  const equipOptionsByKey = useMemo(() => {
    const result = {} as Record<EquipKey, { value: string; count: number }[]>;
    for (const f of EQUIP_FILTERS) result[f.key] = equipOptions(rigs, f.key);
    return result;
  }, [rigs]);

  const pumpCounts = useMemo(() => pumpCountOptions(rigs), [rigs]);

  if (rigsQuery.isLoading) return <LoadingSkeleton lines={8} />;
  if (rigsQuery.error) return <ErrorState message={(rigsQuery.error as Error).message} />;

  const minWaterDepthNum = Number(minWaterDepth) || 0;
  const minHookLoadNum = Number(minHookLoad) || 0;
  const anyFilter =
    category !== '' || rigType !== '' || status !== '' || drillco !== '' || client !== '' ||
    region !== '' || minWaterDepth !== '' || minHookLoad !== '' || activeHeave ||
    mudPumpCount !== '' || Object.values(equip).some((v) => v !== '');

  const resetFilters = () => {
    setCategory('');
    setRigType('');
    setStatus('');
    setDrillco('');
    setClient('');
    setRegion('');
    setMinWaterDepth('');
    setMinHookLoad('');
    setActiveHeave(false);
    setMudPumpCount('');
    setEquip({
      drawworks: '',
      topDrive: '',
      mudPumpModel: '',
      heaveCompensator: '',
      bopModel: '',
      drawworksBrand: '',
      topDriveBrand: '',
      mudPumpBrand: '',
      heaveCompBrand: '',
      bopBrand: '',
    });
  };

  const filtered = rigs
    .filter(
      (r) =>
        matchesCategory(r, category) &&
        (rigType === '' || r.rigType === rigType) &&
        (status === '' || r.status === status) &&
        (drillco === '' || r.drillco.slug === drillco) &&
        (client === '' || r.currentClient?.slug === client) &&
        (region === '' || r.region?.slug === region) &&
        (minWaterDepthNum <= 0 || (r.waterDepthM !== null && r.waterDepthM >= minWaterDepthNum)) &&
        (minHookLoadNum <= 0 || (r.hookLoadT !== null && r.hookLoadT >= minHookLoadNum)) &&
        (!activeHeave || r.hasActiveHeave) &&
        (mudPumpCount === '' || r.mudPumpCount === Number(mudPumpCount)) &&
        EQUIP_FILTERS.every((f) => equip[f.key] === '' || r[f.key] === equip[f.key])
    )
    .sort(
      (a, b) => compareNullsLast(sortValue(a, sort), sortValue(b, sort)) || a.name.localeCompare(b.name)
    );

  const detail = detailQuery.data;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Rig Fleet</CardTitle>
        <CardDescription className="mt-2">
          The tracked offshore fleet, one card per rig — click a card for the full spec sheet and
          contract list. Estimated values render orange, undisclosed ones say so. Equipment
          filters are populated from values found in the fleet's spec sheets.
        </CardDescription>
      </Card>

      {rigs.length === 0 ? (
        <Card>
          <span className="eyebrow">Coming up</span>
          <p className="mt-3 max-w-xl text-muted">
            No rigs tracked yet — the fleet fills as contractor ingests land. Filters, sorting
            and the full spec-sheet modal are already wired up and waiting.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          {/* ── Left filter panel (scrolls independently when taller than the viewport) ── */}
          <aside className="w-full shrink-0 lg:w-64 lg:sticky lg:top-4">
            <div className="bg-surface border border-border rounded-(--radius-md) p-4 shadow-[var(--shadow-sm)] lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Filters</span>
                {anyFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* Category pills */}
              <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Rig category">
                {CATEGORY_PILLS.map((p) => {
                  const active = category === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setCategory(active ? '' : p.key)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-(family-name:--font-ui) transition-colors',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                        active
                          ? 'border-accent bg-accent text-inverse'
                          : 'border-border bg-surface-elevated text-muted hover:text-text'
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-col gap-3">
                <PanelSelect label="Rig type" value={rigType} onChange={setRigType}>
                  {(Object.keys(RIG_TYPE_LABELS) as RigType[]).map((t) => (
                    <option key={t} value={t}>
                      {RIG_TYPE_LABELS[t]}
                    </option>
                  ))}
                </PanelSelect>
                <PanelSelect label="Status" value={status} onChange={setStatus}>
                  {(Object.keys(RIG_STATUS_LABELS) as RigStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {RIG_STATUS_LABELS[s]}
                    </option>
                  ))}
                </PanelSelect>
                <PanelSelect label="Drill co." value={drillco} onChange={setDrillco}>
                  {drillcoOptions.map(([s, n]) => (
                    <option key={s} value={s}>
                      {n}
                    </option>
                  ))}
                </PanelSelect>
                <PanelSelect label="Oil co." value={client} onChange={setClient}>
                  {clientOptions.map(([s, n]) => (
                    <option key={s} value={s}>
                      {n}
                    </option>
                  ))}
                </PanelSelect>
                <PanelSelect label="Region" value={region} onChange={setRegion}>
                  {regionOptions.map(([s, n]) => (
                    <option key={s} value={s}>
                      {n}
                    </option>
                  ))}
                </PanelSelect>

                <PanelNumber
                  label="Min water depth (m)"
                  value={minWaterDepth}
                  onChange={setMinWaterDepth}
                  placeholder="e.g. 1500"
                />
                <PanelNumber
                  label="Min hook load (t)"
                  value={minHookLoad}
                  onChange={setMinHookLoad}
                  placeholder="e.g. 900"
                />

                {/* Equipment dropdowns — AI-canonicalized products found in the fleet */}
                {EQUIP_FILTERS.map((f) => (
                  <PanelSelect
                    key={f.key}
                    label={f.label}
                    value={equip[f.key]}
                    onChange={(v) => setEquip((prev) => ({ ...prev, [f.key]: v }))}
                  >
                    {equipOptionsByKey[f.key].map((o) => (
                      <option key={o.value} value={o.value} title={o.value}>
                        {truncate(o.value)} ({o.count})
                      </option>
                    ))}
                  </PanelSelect>
                ))}

                {pumpCounts.length > 0 && (
                  <PanelSelect label="Mud pumps (count)" value={mudPumpCount} onChange={setMudPumpCount}>
                    {pumpCounts.map((n) => (
                      <option key={n} value={String(n)}>
                        {n}
                      </option>
                    ))}
                  </PanelSelect>
                )}

                <label className="flex items-center gap-2 text-sm text-text">
                  <input
                    type="checkbox"
                    checked={activeHeave}
                    onChange={(e) => setActiveHeave(e.target.checked)}
                    className="h-4 w-4 accent-(--color-accent)"
                  />
                  Active heave compensation
                </label>

                <label className="flex flex-col gap-1">
                  <span className={labelClass}>Sort by</span>
                  <select
                    className={selectClass}
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortKey)}
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <p className="mt-4 border-t border-border pt-3 text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
                {filtered.length} of {rigs.length} rigs
              </p>
            </div>
          </aside>

          {/* ── Card grid ── */}
          <div className="min-w-0 flex-1">
            {filtered.length === 0 ? (
              <Card>
                <CardDescription>No rigs match the current filters.</CardDescription>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {filtered.map((r) => (
                  <RigCard key={r.slug} rig={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {slug !== undefined && (
        <RigModal
          rig={detail}
          loading={detailQuery.isLoading}
          slug={slug}
          onClose={() => navigate('/wwd/rigs')}
        />
      )}
    </div>
  );
}

/* ---------- pieces ---------- */

function PanelSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      <select
        className={cn(selectClass, 'w-full')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">all</option>
        {children}
      </select>
    </label>
  );
}

function PanelNumber({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      <input
        type="number"
        min={0}
        inputMode="numeric"
        className={cn(selectClass, 'w-full')}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function TypeBadge({ rigType }: { rigType: RigType }) {
  return (
    <span className="inline-block rounded-(--radius-sm) border border-accent bg-surface-elevated px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em] text-accent">
      {RIG_TYPE_LABELS[rigType]}
    </span>
  );
}

function StatusChip({ status, statusDetail }: { status: RigStatus; statusDetail: string | null }) {
  return (
    <span
      title={statusDetail ?? undefined}
      className={cn(
        'inline-block rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5',
        'font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]',
        RIG_STATUS_STYLES[status]
      )}
    >
      {RIG_STATUS_LABELS[status]}
    </span>
  );
}

function RigCard({ rig }: { rig: RigCardDto }) {
  return (
    <Link
      to={`/wwd/rigs/${rig.slug}`}
      className={cn(
        'flex flex-col bg-surface border border-border rounded-(--radius-md) p-5 shadow-[var(--shadow-sm)]',
        'transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      )}
    >
      {rig.hasPhoto && (
        <img
          src={`/api/wwd/rigs/${rig.slug}/photo`}
          alt={`${rig.name} — rig photo`}
          loading="lazy"
          className="mb-3 h-36 w-full rounded-(--radius-sm) border border-border object-cover"
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <TypeBadge rigType={rig.rigType} />
        <StatusChip status={rig.status} statusDetail={rig.statusDetail} />
      </div>
      <h3 className="mt-2 text-lg font-semibold text-text">{rig.name}</h3>
      <p className="text-sm text-muted">
        {rig.drillco.name}
        {rig.designClass && <> · {rig.designClass}</>}
      </p>
      <ExNamesLine history={rig.nameHistory} />

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <CardField label="Year built" value={rig.yearBuilt !== null ? String(rig.yearBuilt) : '—'} />
        <CardField
          label="Water depth"
          value={rig.waterDepthM !== null ? `${rig.waterDepthM.toLocaleString('en-US')} m` : '—'}
        />
        <CardField label="Client" value={rig.currentClient?.name ?? '—'} />
        <CardField label="Region" value={rig.region?.name ?? '—'} />
      </dl>
    </Link>
  );
}

/** "~" marks an estimated/unknown adoption date — never rendered as a stated fact. */
function nameDates(seg: NameHistoryItemDto): string | null {
  if (!seg.from && !seg.to) return null;
  const tilde = seg.dateConfidence === 'stated' ? '' : '~';
  return `${tilde}${seg.from ?? '?'}–${seg.to ?? 'today'}`;
}

/** Compact former-names line for the fleet card (full history lives in the modal).
 *  `history ?? []`: tolerates the old-API rolling-deploy window. */
function ExNamesLine({ history }: { history: NameHistoryItemDto[] | undefined }) {
  const ex = (history ?? []).filter((s) => s.to !== null);
  if (ex.length === 0) return null;
  return (
    <p className="mt-0.5 text-xs italic text-muted">
      ex{' '}
      {ex
        .map((s) => {
          const d = nameDates(s);
          return d ? `${s.name} (${d})` : s.name;
        })
        .join(' · ')}
    </p>
  );
}

function CardField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-text">{value}</dd>
    </div>
  );
}

/* ---------- the spec modal ---------- */

function RigModal({
  rig,
  loading,
  slug,
  onClose,
}: {
  rig: RigDetailDto | undefined;
  loading: boolean;
  slug: string;
  onClose: () => void;
}) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={rig ? `${rig.name} — rig details` : 'Rig details'}
    >
      <div className="fixed inset-0 bg-black/60" onClick={onClose} aria-hidden />
      <div className="relative my-auto w-full max-w-3xl bg-surface border border-border rounded-(--radius-md) p-6 shadow-[var(--shadow-md)]">
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Close rig details"
          className="absolute right-4 top-4 rounded-(--radius-sm) p-1 text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {loading ? (
          <>
            <h2 className="pr-8 text-xl font-semibold text-text">Loading rig…</h2>
            <div className="mt-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-4 w-full rounded-(--radius-sm) bg-surface-hover animate-pulse" />
              ))}
            </div>
          </>
        ) : !rig ? (
          <>
            <h2 className="pr-8 text-xl font-semibold text-text">Rig not found</h2>
            <p className="mt-2 text-sm text-muted">
              No rig with slug “{slug}” is in the tracked fleet.
            </p>
          </>
        ) : (
          <>
            {/* Header */}
            {rig.hasPhoto && (
              <div className="mb-4">
                <img
                  src={`/api/wwd/rigs/${rig.slug}/photo`}
                  alt={`${rig.name} — rig photo`}
                  className="max-h-56 w-full rounded-(--radius-sm) border border-border object-cover"
                />
                {rig.photoCredit && (
                  <p className="mt-1 text-xs text-muted">
                    Photo:{' '}
                    {rig.photoPageUrl ? (
                      <a
                        href={rig.photoPageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-text"
                      >
                        {rig.photoCredit}
                      </a>
                    ) : (
                      rig.photoCredit
                    )}
                    {rig.photoOfficial === false && (
                      <span className="ml-1.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                        non-official source
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
            <div className="pr-8">
              <div className="flex flex-wrap items-center gap-2">
                <TypeBadge rigType={rig.rigType} />
                <StatusChip status={rig.status} statusDetail={rig.statusDetail} />
              </div>
              <h2 className="mt-2 font-(family-name:--font-heading) text-2xl font-bold text-text">
                {rig.name}
              </h2>
              <p className="text-sm text-muted">
                {rig.drillco.name}
                {rig.statusDetail && <> — {rig.statusDetail}</>}
              </p>
            </div>

            {/* Full name history (owner directive 2026-06-12) — chronological, oldest
                first; ~dates are estimated/unknown, never stated facts */}
            {(rig.nameHistory ?? []).some((s) => s.to !== null) && (
              <section className="mt-5">
                <span className="eyebrow">Name history</span>
                <ol className="mt-2 flex flex-col gap-1.5 text-sm">
                  {(rig.nameHistory ?? []).map((s, i) => (
                    <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                      <span className={s.to === null ? 'font-semibold text-text' : 'text-text'}>
                        {s.name}
                      </span>
                      {nameDates(s) && (
                        <span className="text-xs text-muted">{nameDates(s)}</span>
                      )}
                      {s.owner && <span className="text-xs text-muted">— {s.owner}</span>}
                      {s.sourceUrl && (
                        <a
                          href={s.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-accent hover:underline"
                        >
                          source ↗
                        </a>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* AIS position mini-map — renders only when the rig has a scraped position */}
            <RigMiniMap slug={rig.slug} />

            {/* Contracts */}
            <section className="mt-6">
              <span className="eyebrow">Contracts</span>
              {rig.contracts.length === 0 ? (
                <p className="mt-2 text-sm text-muted italic opacity-70">
                  No contracts on record.
                </p>
              ) : (
                <div className="mt-3 flex flex-col gap-3">
                  {rig.contracts.map((c, i) => (
                    <ContractRow key={i} contract={c} />
                  ))}
                </div>
              )}
            </section>

            {/* Full spec table — rig.specs arrives in SPEC_FIELD_KEYS (catalog) order */}
            <section className="mt-6">
              <span className="eyebrow">Specifications</span>
              <table className="mt-3 w-full text-sm">
                <tbody>
                  {rig.specs.map((s) => (
                    <tr key={s.key} className="border-b border-border align-top last:border-0">
                      <th
                        scope="row"
                        className="w-44 py-2 pr-3 text-left font-(family-name:--font-ui) text-[0.7rem] font-medium uppercase tracking-[0.12em] text-muted"
                      >
                        {specLabel(s.key)}
                      </th>
                      <td className="py-2 pr-3">
                        <ConfidenceValue value={s.value} confidence={s.confidence} estBasis={s.estBasis} />
                      </td>
                      <td className="w-8 py-2 text-right">
                        {s.source && <SourceLink source={s.source} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Why the orange values — one explanation per estimated field (owner
                directive 2026-06-12). estBasis names the derivation (sister rigs of the
                same verified design class / designer class datasheet) and the source
                link opens the donor document. */}
            {rig.specs.some((s) => s.confidence === 'estimated') && (
              <section className="mt-6 rounded-(--radius-md) border border-amber-500/30 bg-amber-500/5 p-4">
                <span className="eyebrow text-amber-600">Estimated values — why they are shown</span>
                <p className="mt-2 text-xs text-muted">
                  This rig's own documentation does not state the fields below. Each orange
                  value is an estimate derived from the evidence named on its line — typically
                  sister rigs of the same verified design class whose confirmed spec sheets all
                  agree, or the designer's class datasheet. Estimates render orange throughout
                  and are never mixed with stated facts.
                </p>
                <ul className="mt-3 flex flex-col gap-2.5 text-sm">
                  {rig.specs
                    .filter((s) => s.confidence === 'estimated')
                    .map((s) => (
                      <li key={s.key}>
                        <span className="font-medium text-amber-600">{specLabel(s.key)}</span>
                        {' — '}
                        <span className="text-text">{s.value}</span>
                        <p className="mt-0.5 text-xs text-muted">
                          {s.estBasis}
                          {s.source && (
                            <>
                              {' '}
                              <SourceLink source={s.source} />
                            </>
                          )}
                        </p>
                      </li>
                    ))}
                </ul>
              </section>
            )}

            {/* Footer — principle #5: the rig-level data source line */}
            <p className="mt-5 border-t border-border pt-3 text-xs text-muted">
              Data Source:{' '}
              {rig.specSource ? (
                <>
                  <span className="text-text">{rig.specSource.sourceName}</span>{' '}
                  <SourceLink source={rig.specSource} />
                </>
              ) : (
                <span className="italic opacity-70">Not publicly disclosed</span>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function ContractRow({ contract }: { contract: ContractDto }) {
  const client = contract.client?.name ?? contract.clientNameRaw ?? '—';
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-elevated p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-text">{client}</span>
        {contract.isCurrent && (
          <span className="rounded-(--radius-sm) border border-score-high px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-score-high">
            Current
          </span>
        )}
        {contract.isOption && (
          <span className="rounded-(--radius-sm) border border-border px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">
            Option
          </span>
        )}
        <span className="ml-auto">
          <SourceLink source={contract.source} />
        </span>
      </div>

      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
        {contract.periodAsStated && <ContractField label="Period" value={contract.periodAsStated} />}
        {(contract.location || contract.region) && (
          <ContractField label="Location" value={contract.location ?? contract.region?.name ?? ''} />
        )}
        {contract.dayrate && (
          <div>
            <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">
              Dayrate
            </dt>
            <dd className="mt-0.5">
              {/* Reported dayrates MUST carry the linked press source — SourceLink is unconditional. */}
              <ConfidenceValue
                value={`$${contract.dayrate.usdPerDay.toLocaleString('en-US')}/day`}
                confidence={contract.dayrate.confidence}
              />{' '}
              <SourceLink source={contract.dayrate.source} />
            </dd>
          </div>
        )}
        {contract.options && <ContractField label="Options" value={contract.options} />}
        {contract.program && <ContractField label="Program" value={contract.program} />}
      </dl>
    </div>
  );
}

function ContractField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-text">{value}</dd>
    </div>
  );
}
